import { DialogProps } from '@radix-ui/react-dialog';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import { useSettingsStore } from '@/store/use-settings-store';
import { COLLATERAL_DECIMALS, useMarketStore } from '@/store/use-markets-store';
import { useAccountStore, useCurrentAccount } from '@/store/use-account-store';
import PnL from '@/features/portfolio/components/pnl';
import { getIsStatusEval } from '@/features/vest-capital/utils/status';
import {
  Button,
  Checkbox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui';
import { Label } from '@/components/ui/label';
import {
  adjustDecimals,
  bigIntToDecimalStr,
  formatBigInt,
  formatNumber,
  parseDecimalToBigInt,
} from '@/utils/value-format';
import { abbrFromSym } from '@/utils/token-symbol';

import { getBboPrice } from '../utils/math';
import { OrderFormSchema } from './order-form';

interface ClosePositionConfirmProps extends DialogProps {
  onSubmit: () => void;
}

const ClosePositionConfirm = ({
  open,
  onSubmit,
  onOpenChange,
}: ClosePositionConfirmProps) => {
  const form = useFormContext<OrderFormSchema>();
  const { isSubmitting } = form.formState;
  const symbol = form.watch('order.symbol');
  const isBuy = form.watch('order.isBuy');

  const { bboPrice, midPrice, priceDecimals, sizeDecimals } = useStore(
    useMarketStore(symbol),
    useShallow((state) => ({
      bboPrice: getBboPrice(symbol, isBuy),
      midPrice: state.marketData.midPrice,
      priceDecimals: state.marketSpec.priceDecimals,
      sizeDecimals: state.marketSpec.sizeDecimals,
    })),
  );

  const orderSize = parseDecimalToBigInt(
    form.watch('order.size'),
    sizeDecimals,
  );
  const isEval = useAccountStore((state) =>
    getIsStatusEval(state.getCapitalStatus(state.address)),
  );
  const executionPrice = isEval ? bboPrice : midPrice;
  const position = useCurrentAccount((state) => state.positions[symbol]);
  const positionSize = position?.size ?? 0n;
  const positionIsLong = position?.isLong;
  const closeSize = orderSize >= positionSize ? positionSize : orderSize;
  const newPositionSize =
    orderSize > positionSize ? orderSize - positionSize : 0n;
  const unrealizedPnl = useMemo(() => {
    if (!position) return;
    if (!isEval) return position.unrealizedPnl;
    if (!executionPrice) return;

    const pnl =
      (position.isLong ? 1n : -1n) *
      adjustDecimals(
        position.size * executionPrice - position.cost,
        COLLATERAL_DECIMALS - priceDecimals - sizeDecimals,
      );
    const roePct =
      position.initMargin.bigint === 0n
        ? 0n
        : (pnl * 10n ** 4n) / position.initMargin.bigint;

    return {
      pnl: {
        bigint: pnl,
        decimal: bigIntToDecimalStr(pnl, COLLATERAL_DECIMALS),
      },
      roePct: {
        bigint: roePct,
        decimal: bigIntToDecimalStr(roePct, 2n),
      },
    };
  }, [position, isEval, executionPrice, priceDecimals, sizeDecimals]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hideCloseButton className="sm:max-w-md">
        <DialogHeader className="p-5 pb-0">
          <DialogTitle className="text-xl text-foreground">
            Confirm Position Close
          </DialogTitle>
        </DialogHeader>
        <div className="px-5">
          <div className="text-m text-foreground">
            You are about to place an order opposite to your existing position.
          </div>
          <div className="mb-3 mt-4 text-xs uppercase text-vestgrey-500">
            Existing position
          </div>
          <div className="my-1 flex items-center justify-between">
            <p>Side</p>
            <span
              className={`font-mono ${positionIsLong ? 'text-green' : 'text-red'}`}
            >
              {positionIsLong ? 'Long' : 'Short'}
            </span>
          </div>
          <div className="my-1 flex items-center justify-between">
            <p>Size</p>
            <span className="font-mono">
              {formatBigInt(positionSize, sizeDecimals, {
                digits: Number(sizeDecimals),
              })}{' '}
              {abbrFromSym(symbol)}
            </span>
          </div>
          <div className="my-1 flex items-center justify-between">
            <p>PnL</p>
            <span className="font-mono">
              {unrealizedPnl ? <PnL {...unrealizedPnl} /> : '-'}
            </span>
          </div>
          <div className="mb-3 mt-4 text-xs uppercase text-vestgrey-500">
            This order
          </div>
          <div className="my-1 flex items-center justify-between">
            <p>Action</p>
            <span className={`font-mono ${isBuy ? 'text-green' : 'text-red'}`}>
              {isBuy ? 'Buy' : 'Sell'}
            </span>
          </div>
          <div className="my-1 flex items-center justify-between">
            <p>Will close</p>
            <span className="font-mono">
              {formatNumber(bigIntToDecimalStr(closeSize, sizeDecimals), {
                digits: Number(sizeDecimals),
              })}{' '}
              {abbrFromSym(symbol)}
            </span>
          </div>
          <div className="my-1 flex items-center justify-between">
            <p>Value closed</p>
            <span className="font-mono">
              {executionPrice
                ? formatBigInt(
                    adjustDecimals(
                      closeSize * executionPrice,
                      COLLATERAL_DECIMALS - priceDecimals - sizeDecimals,
                    ),
                    COLLATERAL_DECIMALS,
                    { digits: 2 },
                  )
                : '—'}
            </span>
          </div>
          <div className="my-1 flex items-center justify-between">
            <p>% of position</p>
            <span className="font-mono">
              {bigIntToDecimalStr(
                (closeSize * 10000n) / (positionSize <= 0n ? 1n : positionSize),
                2n,
              )}{' '}
              %
            </span>
          </div>
          {newPositionSize > 0n && (
            <>
              <div className="mb-3 mt-4 text-xs uppercase text-vestgrey-500">
                New position
              </div>
              <div className="my-1 flex items-center justify-between">
                <p>Side</p>
                <span
                  className={`font-mono ${isBuy ? 'text-green' : 'text-red'}`}
                >
                  {isBuy ? 'Long' : 'Short'}
                </span>
              </div>
              <div className="my-1 flex items-center justify-between">
                <p>Size</p>
                <span className="font-mono">
                  {formatBigInt(newPositionSize, sizeDecimals, {
                    digits: Number(sizeDecimals),
                  })}{' '}
                  {abbrFromSym(symbol)}
                </span>
              </div>
              <div className="my-1 flex items-center justify-between">
                <p>Value</p>
                <span className="font-mono">
                  {executionPrice
                    ? formatBigInt(
                        adjustDecimals(
                          newPositionSize * executionPrice,
                          COLLATERAL_DECIMALS - priceDecimals - sizeDecimals,
                        ),
                        COLLATERAL_DECIMALS,
                        { digits: 2 },
                      )
                    : '—'}
                </span>
              </div>
            </>
          )}
          <div className="mt-4 flex h-10 items-center gap-3">
            <Checkbox
              id="close-position-confirm"
              className="mt-0.5 size-5"
              onCheckedChange={(checked) =>
                useSettingsStore.setState({
                  showClosePositionConfirm: !checked,
                })
              }
            />
            <Label
              htmlFor="close-position-confirm"
              className="cursor-pointer text-base leading-none text-vestgrey-600"
            >
              Don't show this again
            </Label>
          </div>
        </div>
        <DialogFooter className="grid pt-2 sm:justify-normal">
          <DialogClose asChild>
            <Button
              type="button"
              size="xl"
              className="w-full rounded-none uppercase tracking-wider"
              loading={isSubmitting}
              disabled={isEval && !executionPrice}
              onClick={onSubmit}
            >
              Submit Order
            </Button>
          </DialogClose>
          <DialogClose asChild>
            <Button
              type="button"
              size="xl"
              variant="ghost"
              className="w-full rounded-none uppercase tracking-wider"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ClosePositionConfirm;
